import { subject } from '@casl/ability';
import {
  ActionGroup,
  ColumnFilter,
  createTableColumnHelper,
  dateBetweenFilterFn,
  TableCellLoader,
  TableHeadFilter,
  TableHeadSortButton,
} from '@porsche-code/kado-ui';
import {
  PButton,
  PLinkPure,
  PPagination,
  PTable,
  PTableBody,
  PTableCell,
  PTableHead,
  PTableHeadCell,
  PTableHeadRow,
  PTableRow,
} from '@porsche-design-system/components-react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  columnFacetingFeature,
  columnFilteringFeature,
  ColumnFiltersState,
  columnSizingFeature,
  columnVisibilityFeature,
  createFacetedUniqueValues,
  createFilteredRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  tableFeatures as defineTableFeatures,
  filterFn_equalsString,
  filterFn_includesString,
  flexRender,
  rowPaginationFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_alphanumericCaseSensitive,
  sortFn_basic,
  sortFn_datetime,
  sortFn_text,
  sortFn_textCaseSensitive,
  useTable,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import { DateTimeOutput, SupportRequestStatusTag } from '@/components';
import { NAMESPACES } from '@/config/i18n';
import {
  SupportRequestFilterActionKind,
  SupportRequestFilterSideEffect,
  useAbilityContext,
  useSupportRequestFilterContext,
} from '@/context';
import { SupportRequestStatus, useSupportRequestsQuery } from '@/graphql';
import { useSupportRequestsFilters } from '@/hooks';

import { SupportRequestForm } from './-components';

const features = defineTableFeatures({
  columnFilteringFeature,
  columnFacetingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  rowSortingFeature,
  rowPaginationFeature,
  filterFns: {
    equalsString: filterFn_equalsString,
    includesString: filterFn_includesString,
  },
  sortFns: {
    alphanumeric: sortFn_alphanumeric,
    alphanumericCaseSensitive: sortFn_alphanumericCaseSensitive,
    basic: sortFn_basic,
    datetime: sortFn_datetime,
    text: sortFn_text,
    textCaseSensitive: sortFn_textCaseSensitive,
  },
  filteredRowModel: createFilteredRowModel(),
  sortedRowModel: createSortedRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
  facetedUniqueValues: createFacetedUniqueValues(),
});
const searchSchema = z
  .object({
    limit: z.number(),
    skip: z.number(),
    columnFilters: z.array(
      z.object({
        id: z.string(),
        value: z.unknown().optional(),
      }),
    ),
    sorting: z.array(
      z.object({
        id: z.string(),
        desc: z.boolean(),
      }),
    ),
  })
  .partial();

export const Route = createFileRoute('/support/requests/')({
  component: SupportRequests,
  validateSearch: searchSchema,
  beforeLoad: () => {
    return {
      breadcrumb: () => <Trans i18nKey="supportRequest.requests" />,
    };
  },
});

const defaultRequests: [] = [];

function SupportRequests() {
  const { t } = useTranslation(NAMESPACES);
  const { state: ability } = useAbilityContext();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    dispatch,
    state: { limit = 10, skip = 0, columnFilters, sorting },
  } = useSupportRequestFilterContext();
  const navigate = useNavigate();
  const sideEffect: SupportRequestFilterSideEffect = (state) => {
    setTimeout(
      // Do not interfere while components are still updating
      () =>
        navigate({
          to: '/support/requests',
          search: {
            columnFilters:
              state?.columnFilters &&
              Object.keys(state.columnFilters).length > 0
                ? state.columnFilters
                : undefined,
            sorting:
              state?.sorting && Object.keys(state.sorting).length > 0
                ? state.sorting
                : undefined,
            limit: state?.limit,
            skip: state?.skip,
          },
        }),
      0,
    );
  };

  // Apply filters
  useSupportRequestsFilters();

  const { data: queryData, isPending } = useSupportRequestsQuery();

  const columns = useMemo(() => {
    const columnHelper = createTableColumnHelper<
      typeof features,
      NonNullable<typeof queryData>['supportRequests'][number]
    >();

    return columnHelper.columns([
      columnHelper.accessor('id', {
        header: t('supportRequest.ticketId'),
        enableSorting: false,
        cell: (data) => (
          <LinkedCell
            id={data.row.original?.id}
            unread={data.row.original.hasNewActivity}
            value={data.getValue()}
          />
        ),
      }),
      columnHelper.accessor('createdAt', {
        header: t('common:date'),
        cell: (data) => (
          <LinkedCell
            id={data.row.original?.id}
            unread={data.row.original.hasNewActivity}
            value={<DateTimeOutput date={data.getValue()} />}
          />
        ),
        filterFn: dateBetweenFilterFn,
        meta: {
          filterType: 'date',
        },
      }),
      columnHelper.accessor('title', {
        header: t('supportRequest.title'),
        cell: (data) => (
          <LinkedCell
            id={data.row.original?.id}
            unread={data.row.original.hasNewActivity}
            value={data.getValue()}
          />
        ),
      }),
      columnHelper.accessor('organization.name', {
        header: t('common:organization'),
        cell: (data) => (
          <LinkedCell
            id={data.row.original?.id}
            unread={data.row.original.hasNewActivity}
            value={data.getValue()}
          />
        ),
      }),
      // Only show author column if user can see other items than their own
      ...(ability.can(
        'read',
        subject('SupportRequest', {
          author: 0,
        }),
      )
        ? [
            columnHelper.accessor('author.name', {
              header: t('common:person'),
              cell: (data) => (
                <LinkedCell
                  id={data.row.original?.id}
                  unread={data.row.original.hasNewActivity}
                  value={data.getValue()}
                />
              ),
            }),
          ]
        : []),
      columnHelper.accessor('status', {
        header: t('common:status'),
        cell: (data) => (
          <LinkedCell
            id={data.row.original?.id}
            value={<SupportRequestStatusTag status={data.getValue()} />}
          />
        ),
        meta: {
          name: t('common:status'),
          filterType: 'select',
          filterOptions: Object.values(SupportRequestStatus).map((status) => ({
            label: t(`supportRequest.statusValues.${status}`),
            value: status,
          })),
        },
        filterFn: 'equalsString',
      }),
    ]);
  }, [t, ability]);

  const pagination = useMemo(
    () => ({ pageSize: limit, pageIndex: skip }),
    [limit, skip],
  );

  const table = useTable({
    features,
    data: queryData?.supportRequests ?? defaultRequests,
    columns,
    autoResetPageIndex: false,
    state: {
      sorting,
      pagination,
      columnFilters: columnFilters as ColumnFiltersState,
    },
    onSortingChange: (updateOrValue) => {
      dispatch({
        type: SupportRequestFilterActionKind.Set,
        payload: {
          limit: 10,
          skip: 0,
          sorting:
            typeof updateOrValue === 'function'
              ? updateOrValue(sorting ?? [])
              : updateOrValue,
        },
        sideEffect,
      });
    },
    onPaginationChange: (updateOrValue) => {
      const value =
        typeof updateOrValue === 'function'
          ? updateOrValue(pagination)
          : updateOrValue;
      dispatch({
        type: SupportRequestFilterActionKind.Set,
        payload: {
          limit: value.pageSize,
          skip: value.pageIndex,
        },
        sideEffect,
      });
    },
    onColumnFiltersChange: (updateOrValue) => {
      dispatch({
        type: SupportRequestFilterActionKind.Set,
        payload: {
          limit: 10,
          skip: 0,
          columnFilters:
            typeof updateOrValue === 'function'
              ? updateOrValue((columnFilters as ColumnFiltersState) ?? [])
              : updateOrValue,
        },
        sideEffect,
      });
    },
  });

  return (
    <>
      <PTable role="table" caption={t('supportRequest.requests')}>
        <PTableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <PTableHeadRow key={headerGroup.id}>
              {headerGroup.headers.map((header, i) => {
                const isSorted = header.column.getIsSorted();

                return (
                  <PTableHeadCell
                    key={`${header.id}_${i}`}
                    style={{
                      width: `${
                        header.column.id === 'selectColumn' ? '20px' : 'auto'
                      }`,
                      minWidth: header.column.getSize(),
                    }}
                  >
                    {header.column.getCanSort() ? (
                      <TableHeadSortButton
                        onClick={header.column.getToggleSortingHandler()}
                        direction={
                          isSorted ||
                          header.column.getNextSortingOrder() ||
                          undefined
                        }
                        isActive={!!isSorted}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </TableHeadSortButton>
                    ) : (
                      <>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </>
                    )}

                    {header.column.getCanFilter() && (
                      <TableHeadFilter>
                        <ColumnFilter
                          column={header.column}
                          i18n={{
                            filterLabel: t('common:iconLabel.filter', {
                              columnName:
                                header.column.columnDef.meta?.name ??
                                header.column.id,
                            }),
                            optionAll: t('common:all'),
                            buttonReset: t('common:action.reset'),
                            buttonFilter: t('common:action.filter'),
                            actionSearch: t('common:action.search'),
                            minLabel: t('common:min'),
                            maxLabel: t('common:max'),
                          }}
                        />
                      </TableHeadFilter>
                    )}
                  </PTableHeadCell>
                );
              })}
            </PTableHeadRow>
          ))}
        </PTableHead>
        <PTableBody>
          {table.getRowModel().rows.map((row) => (
            <PTableRow role="row" key={row.id}>
              {row.getVisibleCells().map((cell, i) => {
                const testId = cell.column.columnDef.meta?.testId;
                const onClick = cell.column.columnDef.meta?.onClick;

                return (
                  <PTableCell
                    role="cell"
                    {...(onClick && {
                      onClick: () => onClick(row.original),
                    })}
                    key={`${cell.id}_${i}`}
                    style={{
                      minWidth: cell.column.getSize(),
                      verticalAlign: 'top',
                      ...(onClick && { cursor: 'pointer' }),
                    }}
                    {...(testId && { 'data-testId': testId })}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </PTableCell>
                );
              })}
            </PTableRow>
          ))}

          {isPending &&
            Array.from({ length: pagination.pageSize }).map((_, index) => (
              <PTableRow key={`TableCellLoader_${index}`}>
                {table.getVisibleLeafColumns().map((col) => (
                  <PTableCell key={col.id}>
                    <TableCellLoader />
                  </PTableCell>
                ))}
              </PTableRow>
            ))}
        </PTableBody>
      </PTable>
      <ActionGroup>
        <PButton
          aria={{ 'aria-haspopup': 'dialog' }}
          type="button"
          name={t('supportRequest.createRequest')}
          icon="add"
          onClick={() => {
            setIsModalOpen(true);
          }}
        >
          {t('supportRequest.createRequest')}
        </PButton>

        <PPagination
          totalItemsCount={table.getPageCount() * 10}
          itemsPerPage={10}
          activePage={table.state.pagination.pageIndex + 1}
          onUpdate={(event) => {
            table.setPageIndex(event.detail.page - 1);
          }}
        />
      </ActionGroup>
      <SupportRequestForm
        isOpen={isModalOpen}
        onDismiss={() => setIsModalOpen(false)}
      />
    </>
  );
}

const LinkedCell = ({
  id,
  unread,
  value,
}: {
  id?: string;
  unread?: boolean;
  value: React.ReactNode;
}) => (
  <PLinkPure icon="none" stretch>
    <Link to="/support/requests/$requestId" params={{ requestId: id ?? '' }}>
      {unread ? <strong>{value}</strong> : value}
    </Link>
  </PLinkPure>
);
